import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { 
  AlertDialog, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface DeleteVideoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoTitle: string;
  onConfirm: () => Promise<void>;
}

export function DeleteVideoModal({
  open,
  onOpenChange,
  videoTitle,
  onConfirm
}: DeleteVideoModalProps) {
  useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para excluir vídeos"));
      onOpenChange(false);
    }
  }, [open, canEdit, onOpenChange]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      // Toast já foi exibido no handler
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Remover foco de qualquer elemento ao fechar
    setTimeout(() => {
      (document.activeElement as HTMLElement)?.blur();
    }, 0);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[92vw] sm:w-[clamp(360px,38vw,440px)] sm:max-w-none p-0 rounded-2xl">
        <AlertDialogHeader className="px-5 py-4">
          <AlertDialogTitle>
            {translateInterfaceText("Excluir vídeo?")}</AlertDialogTitle>
        </AlertDialogHeader>

        <div className="px-5 pb-4">
          <AlertDialogDescription>
            {translateInterfaceText("Tem certeza que deseja excluir permanentemente o vídeo")}{' '}
            <strong className="inline-block max-w-[250px] truncate align-bottom" title={videoTitle}>
              "{videoTitle}"
            </strong>
            {translateInterfaceText("? Esta ação não pode ser desfeita.")}</AlertDialogDescription>
        </div>

        <AlertDialogFooter className="px-5 py-4 border-t">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            autoFocus
            className="text-muted-foreground hover:bg-muted hover:text-[#64748b] transition-colors"
          >
            {translateInterfaceText("Cancelar")}</Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {translateInterfaceText("Excluir")}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
